import * as React from "react";
import { userHasPermission } from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";

/**
 * Phase SC-2b — single source of truth for the IA-side owner-finances
 * (accounting) gate.
 *
 * Mirrors `useCanEditSeating` (RBAC-only, `useAuth`-pure, unit-testable).
 * The accounting surface is deliberately admin-ceiling: a study centre's
 * profitability is not front-desk data. The BE re-checks
 * `view_accounting` (reads) / `manage_accounting` (writes) on every call,
 * so this hook is a UX layer, not the only line of defence.
 *
 * Returns:
 *   - `canViewAccounting` — read-eligibility (P&L + ledger listing).
 *   - `canManageAccounting` — write-eligibility (create/edit/delete). The
 *     "Add expense" / row actions render only when `true`.
 *   - `missingView` — logged in but lacks even `view_accounting`.
 */
export interface AccountingAuthoringGate {
  canViewAccounting: boolean;
  canManageAccounting: boolean;
  missingView: boolean;
}

export function useCanManageAccounting(): AccountingAuthoringGate {
  const { user } = useAuth();
  return React.useMemo<AccountingAuthoringGate>(() => {
    if (!user) {
      return { canViewAccounting: false, canManageAccounting: false, missingView: false };
    }
    const overrides = user.permission_overrides ?? [];
    const canView = userHasPermission(
      user.role,
      overrides,
      "view_accounting",
      user.permission_mode ?? "additive",
    );
    const canManage = userHasPermission(
      user.role,
      overrides,
      "manage_accounting",
      user.permission_mode ?? "additive",
    );
    return {
      canViewAccounting: canView,
      canManageAccounting: canManage,
      missingView: !canView,
    };
  }, [user]);
}
